// PartnershipEditor — per-agent win/loss partnership % editor (hierarchy
// restructure H7). Replaces the old read-only "Pioneer Split" rate views.
//
// Talks to the H3 backend, mounted at /api/admin/partnership on BOTH the
// admin and agent apps:
//   GET  /admin/partnership/:agentId → { winLossPct, parentPct, maxAllowed, levelName, username }
//   POST /admin/partnership/:agentId  { pct } → { rate:{winLossPct}, events:[...] }
//
// Golden Limit (enforced server-side; mirrored in the UI): a child's % is
// always strictly less than its parent's. The server returns `maxAllowed`
// (= parent% − 0.01, or 100 for a top-level agent); the input is clamped
// to it and the server rejects violations with code `exceeds_parent`.
//
// Scope is enforced server-side too: an admin may set anyone; an agent may
// set only its own direct children (else `not_your_child`). Pass
// readOnly for a pure display (e.g. an agent viewing its OWN rate, which
// only its parent/admin may change).

import { useEffect, useState, useCallback } from 'react';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import Icon from '@baaligames/shared-ui/components/Icon';

export type PartnershipData = {
  agentId: number;
  username?: string;
  level?: number;
  levelName?: string;
  winLossPct: number;
  parentPct: number;
  maxAllowed: number;
};

type ClampEvent = { agentId: number; old: number; new: number; cause: string };

export default function PartnershipEditor({
  agentId,
  readOnly = false,
  onSaved,
}: {
  agentId: number;
  readOnly?: boolean;
  onSaved?: (winLossPct: number) => void;
}) {
  const [data, setData] = useState<PartnershipData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [input, setInput] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [events, setEvents] = useState<ClampEvent[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const d = await adminApi.get<PartnershipData>(`/admin/partnership/${agentId}`);
      setData(d);
      setInput(String(d.winLossPct ?? 0));
    } catch (e) {
      setLoadErr((e as ApiError).message || 'Failed to load partnership rate.');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { void load(); }, [load]);

  const pct = Number(input);
  const valid = Number.isFinite(pct) && pct >= 0 && data != null && pct <= data.maxAllowed;
  const dirty = data != null && pct !== Number(data.winLossPct);

  async function save() {
    if (!data || !valid || saving) return;
    setSaving(true);
    setSaveErr(null);
    setEvents(null);
    try {
      const out = await adminApi.post<{ rate: { winLossPct: number }; events: ClampEvent[] }>(
        `/admin/partnership/${agentId}`,
        { pct },
      );
      setEvents(out.events || []);
      onSaved?.(out.rate.winLossPct);
      await load();
    } catch (e) {
      setSaveErr((e as ApiError).message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="text-[12.5px] text-arena-600 dark:text-neon-200/70">Loading partnership rate…</div>;
  }
  if (loadErr || !data) {
    return (
      <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 p-3 text-[12.5px] text-rose-700 dark:text-rose-300 ring-1 ring-inset ring-rose-200/60">
        {loadErr || 'No partnership data.'}
      </div>
    );
  }

  const isTop = data.parentPct >= 100 && data.maxAllowed >= 100;

  return (
    <div className="space-y-3">
      <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2">
        <Icon name="account_tree" size={16} className="text-violet-600 dark:text-violet-300" />
        Partnership rate — Win/Loss %
      </h3>

      {/* Current / ceiling summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-arena-50 dark:bg-white/5 p-3 ring-1 ring-inset ring-arena-200/60 dark:ring-white/10">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-arena-600 dark:text-neon-200/70">Current share</div>
          <div className="font-black text-[20px] text-neutral-900 dark:text-white">{Number(data.winLossPct)}%</div>
          {data.levelName && (
            <div className="text-[11px] text-arena-600 dark:text-neon-200/60">{data.levelName}{data.username ? ` · @${data.username}` : ''}</div>
          )}
        </div>
        <div className="rounded-xl bg-violet-50/70 dark:bg-violet-500/10 p-3 ring-1 ring-inset ring-violet-200/60 dark:ring-violet-400/20">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">Max allowed</div>
          <div className="font-black text-[20px] text-violet-700 dark:text-violet-200">{Number(data.maxAllowed)}%</div>
          <div className="text-[11px] text-violet-700/80 dark:text-violet-300/70">
            {isTop ? 'Top level — set by admin' : `Parent holds ${Number(data.parentPct)}% (child must stay below)`}
          </div>
        </div>
      </div>

      {readOnly ? (
        <p className="text-[12px] text-arena-600 dark:text-neon-200/70">
          This rate is set by your upline / admin and can’t be edited here.
        </p>
      ) : (
        <div className="space-y-2">
          <label className="block text-[10.5px] font-bold uppercase tracking-wide text-arena-600 dark:text-neon-200/70">
            Set new share (0–{Number(data.maxAllowed)}%)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number" inputMode="decimal" min={0} max={data.maxAllowed} step={0.01}
              value={input}
              onChange={(e) => { setInput(e.target.value); setSaveErr(null); setEvents(null); }}
              className="w-32 rounded-lg border border-arena-300 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2 text-[15px] font-semibold text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
            <span className="text-[15px] font-semibold text-arena-600 dark:text-neon-200/70">%</span>
            <button
              type="button"
              disabled={!valid || !dirty || saving}
              onClick={() => void save()}
              className="ml-auto rounded-lg bg-violet-600 px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-violet-700 transition-colors"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {!valid && input !== '' && (
            <div className="text-[12px] font-semibold text-rose-600 dark:text-rose-400">
              Must be between 0 and {Number(data.maxAllowed)}% (below the parent’s share).
            </div>
          )}
          {saveErr && (
            <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 p-2 text-[12px] font-semibold text-rose-700 dark:text-rose-300 ring-1 ring-inset ring-rose-200/60">
              {saveErr}
            </div>
          )}
          {events && (
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-500/10 p-2 text-[12px] text-emerald-800 dark:text-emerald-300 ring-1 ring-inset ring-emerald-200/60">
              <div className="font-bold">Saved.</div>
              {events.filter((e) => e.cause === 'cascade_clamp').length > 0 && (
                <div className="mt-1">
                  Downline auto-clamped to stay below:
                  <ul className="mt-0.5 list-disc list-inside">
                    {events.filter((e) => e.cause === 'cascade_clamp').map((e) => (
                      <li key={e.agentId}>agent #{e.agentId}: {e.old}% → {e.new}%</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
